import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseService } from "@core/services/base.service";
import { LoadingService } from "@core/services/loading.service";
import { environment } from "@env/environment";
import {
  ThreadInterface,
  ThreadListResponseInterface,
  ComposeMessageRequestInterface,
  ReplyMessageRequestInterface,
  BatchUpdateRequestInterface,
  UnreadCountResponseInterface,
  ThreadNavigationInterface,
  RecipientSearchResponseInterface,
  MessageInterface
} from "@features/messages/interfaces/messages.interface";
import { Observable } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class MessagesApiService extends BaseService {
  private readonly apiUrl = `${environment.classicApiUrl}/api/v2/common/messages`;

  constructor(
    public readonly loadingService: LoadingService,
    private readonly http: HttpClient
  ) {
    super(loadingService);
  }

  getThreads(
    view: "inbox" | "trash",
    page: number = 1,
    unreadOnly?: boolean,
    searchTerm?: string
  ): Observable<ThreadListResponseInterface> {
    let params = new HttpParams().set("view", view).set("page", page.toString());

    // Only apply the filter for inbox view, as per API documentation
    if (view === "inbox" && unreadOnly === true) {
      params = params.set("only_unread", "true");
    }

    // Add search query if provided
    if (searchTerm && searchTerm.trim() !== "") {
      params = params.set("q", searchTerm.trim());
    }

    return this.http.get<ThreadListResponseInterface>(`${this.apiUrl}/threads/`, { params });
  }

  getThread(id: number): Observable<ThreadInterface> {
    return this.http.get<ThreadInterface>(`${this.apiUrl}/threads/${id}/`);
  }

  composeMessage(data: ComposeMessageRequestInterface): Observable<ThreadInterface> {
    return this.http.post<ThreadInterface>(`${this.apiUrl}/compose/`, data);
  }

  replyToThread(threadId: number, data: ReplyMessageRequestInterface): Observable<MessageInterface> {
    return this.http.post<MessageInterface>(`${this.apiUrl}/threads/${threadId}/reply/`, data);
  }

  deleteThread(threadId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/threads/${threadId}/delete-thread/`, {});
  }

  markThreadAsRead(threadId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/threads/${threadId}/mark-read/`, {});
  }

  markThreadAsUnread(threadId: number): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/threads/${threadId}/mark-unread/`, {});
  }

  batchUpdateThreads(data: BatchUpdateRequestInterface): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/threads/batch-update/`, data);
  }

  getUnreadCount(): Observable<UnreadCountResponseInterface> {
    return this.http.get<UnreadCountResponseInterface>(`${this.apiUrl}/unread-count/`);
  }

  getThreadNavigation(threadId: number): Observable<ThreadNavigationInterface> {
    return this.http.get<ThreadNavigationInterface>(`${this.apiUrl}/threads/${threadId}/navigation/`);
  }

  searchRecipients(query: string): Observable<RecipientSearchResponseInterface> {
    const params = new HttpParams().set("q", query);
    return this.http.get<RecipientSearchResponseInterface>(`${this.apiUrl}/recipients/search/`, { params });
  }
}
